/**
 * Zendesk-only console debug for live-chat ticket create.
 * Never logs Bearer tokens or API keys.
 */

import { getBearerAuthHeaders } from "@/lib/authSession";
import { fetchWithAuthRetry } from "@/lib/authedRequest";
import { fetchWithTimeout } from "@/lib/fetchWithTimeout";
import { getInboxAgentWorkspaceId, inboxWorkspaceHeaders } from "@/lib/inboxLiveConfig";

const PREFIX = "[zendesk-ticket]";
/** Backend RPC wait is ZENDESK_API_TIMEOUT_SECONDS (60). Do not abort first. */
export const ZENDESK_FROM_LIVE_CHAT_TIMEOUT_MS = 90000;

export class ZendeskTicketCreateDebug {
  static _enabledCache = new Map();

  constructor() {
    this._cache = ZendeskTicketCreateDebug._enabledCache;
  }

  _workspaceId(workspaceId) {
    if (workspaceId && String(workspaceId).trim()) return String(workspaceId).trim();
    return getInboxAgentWorkspaceId();
  }

  async isEnabled(workspaceId) {
    const wid = this._workspaceId(workspaceId);
    if (!wid) return false;
    if (this._cache.has(wid)) return this._cache.get(wid);
    try {
      const buildRequest = () => {
        const url = new URL(
          "/api/v1/agent/tickets/zendesk-enabled",
          import.meta.env?.VITE_API_BASE_URL || window.location.origin,
        );
        return fetchWithTimeout(
          url.toString(),
          {
            method: "GET",
            credentials: "include",
            headers: {
              Accept: "application/json",
              ...getBearerAuthHeaders(),
              ...inboxWorkspaceHeaders(wid),
            },
          },
          15000,
        );
      };
      const res = await fetchWithAuthRetry(buildRequest);
      if (!res.ok) {
        return false;
      }
      const payload = await res.json();
      const enabled = Boolean(payload?.enabled);
      this._cache.set(wid, enabled);
      return enabled;
    } catch {
      return false;
    }
  }

  logStart({ workspaceId, conversationId }) {
    console.debug(PREFIX, "create start", { workspaceId, conversationId });
  }

  logResult({ status, durationMs, detail, requestId }) {
    console.debug(PREFIX, "create result", {
      status,
      durationMs,
      error: detail?.error,
      message: detail?.message,
      requestId: requestId || undefined,
    });
  }

  logOk({ ticketId }) {
    console.debug(PREFIX, "create ok", { ticketId });
  }

  parseDetail(body) {
    const raw = body?.detail ?? body;
    if (!raw) return null;
    if (typeof raw === "string") return { message: raw };
    if (typeof raw === "object") {
      return {
        error: raw.error || undefined,
        message: raw.message || undefined,
      };
    }
    return null;
  }
}
